/* eslint-disable react/require-default-props */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { FaChevronRight } from 'react-icons/fa';

import { SmallButton } from '../../../../styles/ui/Button';
import GuardedAction from './GuardedAction';
import { EMPTY_PDV_ACCESS } from '../utils/pdvAccess';
import { ShipmentStatusBadge } from './StatusBadges';
import { SHIPMENT_STATUS } from '../constants';
import { formatDateTimeBr, formatQty, productLabel } from '../utils/format';

// =============================================================================
// Card de remessa — usado no detalhe da ordem. COLAPSÁVEL desde a refatoração
// visual de 2026-09-10: fechado mostra o resumo (nº, situação, responsável,
// datas, total de unidades); aberto, o confronto expedido → conferido item a
// item, com o texto da divergência (vermelho) e o da tratativa (verde).
//
// As ações continuam derivadas do ESTADO da remessa e das permissões, nunca só
// da permissão: conferir e cancelar só existem em trânsito; tratativa só com
// divergência pendente. Barreira de PDV via GuardedAction (tooltip no wrapper).
//
// Abre por padrão quando ainda há trabalho: em trânsito ou divergência sem
// tratativa. Conferida sem pendência e cancelada nascem fechadas.
// =============================================================================

const Card = styled.article`
   border: 1px solid rgba(0, 0, 0, 0.12);
   border-radius: 12px;
   background: #fff;
   margin-bottom: 12px;
   overflow: hidden;
`;

const CardHead = styled.div`
   display: flex;
   align-items: center;
   gap: 10px;
   padding: 12px 14px;
   cursor: pointer;

   &:hover {
      background: #f7fafd;
   }
`;

const ChevronBtn = styled.button`
   appearance: none;
   border: 0;
   background: transparent;
   padding: 0;
   width: 20px;
   height: 20px;
   flex-shrink: 0;
   display: inline-flex;
   align-items: center;
   justify-content: center;
   cursor: pointer;
   color: ${({ theme }) => theme.colors.textMuted};
   transition: transform 0.15s ease;
   transform: ${({ $open }) => ($open ? 'rotate(90deg)' : 'none')};

   /* G.4.1: hover sem background próprio herdaria o azul do legado. */
   &:hover:not(:disabled) {
      background: transparent;
      color: ${({ theme }) => theme.colors.textPrimary};
   }
   &:focus-visible {
      outline: none;
      box-shadow: 0 0 0 3px ${({ theme }) => theme.colors.focusRing};
      border-radius: 5px;
   }
`;

const HeadText = styled.div`
   flex: 1;
   min-width: 0;

   .title-row {
      display: flex;
      align-items: center;
      gap: 8px;
      flex-wrap: wrap;
   }
   .title {
      font-size: 14px;
      font-weight: 700;
      color: ${({ theme }) => theme.colors.textPrimary};
   }
   .meta {
      margin-top: 2px;
      font-size: 12px;
      color: ${({ theme }) => theme.colors.textMuted};
      overflow-wrap: anywhere;
   }
`;

const HeadRight = styled.div`
   flex-shrink: 0;
   display: flex;
   align-items: center;
   gap: 10px;

   .total {
      font-size: 13px;
      font-weight: 800;
      white-space: nowrap;
      font-variant-numeric: tabular-nums;
      color: ${({ theme }) => theme.colors.textPrimary};
   }
`;

const CardActions = styled.div`
   display: flex;
   gap: 8px;
   flex-wrap: wrap;
`;

const CardBody = styled.div`
   border-top: 1px solid ${({ theme }) => theme.colors.borderLight};
   padding: 12px 14px 14px 44px;

   @media (max-width: 640px) {
      padding-left: 14px;
   }
`;

// Tarja de reversão da remessa cancelada — é AQUI que o estorno é contado; o
// movimento de reversão em si não é listável (recurso de sistema).
const CancelNote = styled.p`
   margin: 0 0 12px;
   padding: 8px 10px;
   border-radius: 8px;
   border: 1px solid rgba(211, 47, 47, 0.3);
   background: rgba(211, 47, 47, 0.08);
   font-size: 12.5px;
   line-height: 1.5;
   color: #8e1b1b;
`;

const NotesLine = styled.p`
   margin: 0 0 12px;
   font-size: 12.5px;
   color: ${({ theme }) => theme.colors.textMuted};
`;

const ItemLine = styled.div`
   padding: 9px 0;
   border-top: 1px solid ${({ theme }) => theme.colors.borderLight};

   &:first-of-type {
      border-top: none;
      padding-top: 0;
   }

   .main {
      display: flex;
      align-items: baseline;
      justify-content: space-between;
      gap: 12px;
   }
   .name {
      min-width: 0;
      font-size: 13px;
      font-weight: 600;
      color: ${({ theme }) => theme.colors.textPrimary};
      overflow-wrap: anywhere;
   }
   .qty {
      flex-shrink: 0;
      font-size: 13px;
      font-weight: 800;
      font-variant-numeric: tabular-nums;
      white-space: nowrap;
      color: ${({ $tone }) => {
         if ($tone === 'missing') return '#a01b1b';
         if ($tone === 'ok') return '#1b5e20';
         return 'rgba(0, 0, 0, 0.55)';
      }};
   }

   .divergence {
      margin-top: 3px;
      font-size: 12px;
      line-height: 1.45;
      color: #a01b1b;
   }
   .settlement {
      margin-top: 2px;
      font-size: 12px;
      line-height: 1.45;
      color: #1b5e20;
   }
   .pending {
      margin-top: 2px;
      font-size: 12px;
      font-weight: 700;
      color: #8a5a00;
   }
`;

function metaLine(shipment) {
   const parts = [];
   // Responsável é employee e pode vir null (opcional desde o handoff 0108) —
   // sem responsável designado, a linha simplesmente não o menciona.
   if (shipment.responsible_employee_name)
      parts.push(shipment.responsible_employee_name);
   if (shipment.dispatched_at)
      parts.push(`expedida ${formatDateTimeBr(shipment.dispatched_at)}`);
   if (shipment.received_at)
      parts.push(`conferida ${formatDateTimeBr(shipment.received_at)}`);
   return parts.join(' · ') || '—';
}

function lineTone(shipment, item) {
   if (shipment.status === SHIPMENT_STATUS.DISPATCHED) return 'transit';
   if (Number(item.divergent_qtd) > 0) return 'missing';
   return 'ok';
}

export default function ShipmentCard({
   shipment,
   canReceive = false,
   canCancel = false,
   canSettle = false,
   pdvAccess = EMPTY_PDV_ACCESS,
   onReceive,
   onCancel,
   onSettle,
}) {
   const isDispatched = shipment.status === SHIPMENT_STATUS.DISPATCHED;
   const isCancelled = shipment.status === SHIPMENT_STATUS.CANCELLED;
   const items = shipment.items ?? [];

   const hasPendingDivergence = items.some(
      (item) => Number(item.divergent_qtd) > 0 && !item.divergence_settled_at
   );

   const [open, setOpen] = useState(isDispatched || hasPendingDivergence);
   const toggle = () => setOpen((prev) => !prev);

   const totalUnits = items.reduce(
      (acc, item) => acc + (Number(item.shipped_qtd) || 0),
      0
   );
   const bodyId = `shipment-body-${shipment.id}`;

   return (
      <Card>
         <CardHead onClick={toggle}>
            <ChevronBtn
               type="button"
               $open={open}
               aria-expanded={open}
               aria-controls={bodyId}
               aria-label={`Detalhar remessa ${shipment.id}`}
               onClick={(event) => {
                  event.stopPropagation();
                  toggle();
               }}
            >
               <FaChevronRight size={11} />
            </ChevronBtn>

            <HeadText>
               <div className="title-row">
                  <span className="title">Remessa #{shipment.id}</span>
                  <ShipmentStatusBadge value={shipment.status} />
               </div>
               <div className="meta">{metaLine(shipment)}</div>
            </HeadText>

            <HeadRight onClick={(event) => event.stopPropagation()}>
               <CardActions>
                  {/* Conferir exige o DESTINO; cancelar remessa, a ORIGEM;
                      tratativa aceita qualquer ponta (trava só sem as duas). */}
                  {isDispatched && canReceive && (
                     <GuardedAction reason={pdvAccess.blockedForDest}>
                        <SmallButton
                           type="button"
                           $intent="write"
                           onClick={() => onReceive?.(shipment)}
                        >
                           Conferir
                        </SmallButton>
                     </GuardedAction>
                  )}
                  {isDispatched && canCancel && (
                     <GuardedAction reason={pdvAccess.blockedForOrigin}>
                        <SmallButton
                           type="button"
                           $intent="danger"
                           onClick={() => onCancel?.(shipment)}
                        >
                           Cancelar remessa
                        </SmallButton>
                     </GuardedAction>
                  )}
                  {hasPendingDivergence && canSettle && (
                     <GuardedAction reason={pdvAccess.blockedForEither}>
                        <SmallButton
                           type="button"
                           $intent="write"
                           onClick={() => onSettle?.(shipment)}
                        >
                           Registrar tratativa
                        </SmallButton>
                     </GuardedAction>
                  )}
               </CardActions>
               <span className="total">{totalUnits} un</span>
            </HeadRight>
         </CardHead>

         {open && (
            <CardBody id={bodyId}>
               {isCancelled && (
                  <CancelNote>
                     Cancelada em {formatDateTimeBr(shipment.cancelled_at)} — o
                     estoque voltou à origem por um movimento de reversão.
                     {shipment.cancelled_reason
                        ? ` Motivo: ${shipment.cancelled_reason}`
                        : ''}
                  </CancelNote>
               )}

               {shipment.notes && (
                  <NotesLine>Observações: {shipment.notes}</NotesLine>
               )}

               {items.map((item) => {
                  const divergent = Number(item.divergent_qtd) > 0;
                  return (
                     <ItemLine key={item.prod_id} $tone={lineTone(shipment, item)}>
                        <div className="main">
                           <span className="name">{productLabel(item)}</span>
                           <span
                              className="qty"
                              title="Expedido → conferido (o destino entra pelo contado)"
                           >
                              {item.shipped_qtd} →{' '}
                              {isDispatched ? '?' : formatQty(item.received_qtd)}
                           </span>
                        </div>

                        {divergent && (
                           <>
                              <div className="divergence">
                                 Falta {item.divergent_qtd}
                                 {item.divergence_reason
                                    ? ` — ${item.divergence_reason}`
                                    : ''}
                              </div>
                              {item.divergence_settled_at ? (
                                 <div className="settlement">
                                    Tratativa
                                    {item.divergence_settled_by_name
                                       ? ` (${item.divergence_settled_by_name}, ${formatDateTimeBr(item.divergence_settled_at)})`
                                       : ` (${formatDateTimeBr(item.divergence_settled_at)})`}
                                    {item.divergence_settlement
                                       ? `: ${item.divergence_settlement}`
                                       : '.'}
                                 </div>
                              ) : (
                                 <div className="pending">
                                    Tratativa pendente.
                                 </div>
                              )}
                           </>
                        )}
                     </ItemLine>
                  );
               })}
            </CardBody>
         )}
      </Card>
   );
}

ShipmentCard.propTypes = {
   // eslint-disable-next-line react/forbid-prop-types
   shipment: PropTypes.object.isRequired,
   canReceive: PropTypes.bool,
   canCancel: PropTypes.bool,
   canSettle: PropTypes.bool,
   pdvAccess: PropTypes.shape({
      blockedForOrigin: PropTypes.string,
      blockedForDest: PropTypes.string,
      blockedForEither: PropTypes.string,
   }),
   onReceive: PropTypes.func,
   onCancel: PropTypes.func,
   onSettle: PropTypes.func,
};
